import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useLoaderData } from 'react-router-dom';
import { redirectErrorPage } from '../../utils/errors';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter, useFormatters } from '../../utils/texts';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import PersonMatrix from '../../components/common/PersonMatrix/PersonMatrix';
import type {
    PersonMatrixItem,
    PersonMatrixGroup,
    PersonMatrixLegendGroup,
    PersonMatrixLegendItem
} from '../../components/common/PersonMatrix/PersonMatrix';
import { useVoteColors } from '../../utils/hooks';
import { getColors } from '../../Constants';
import type { VoteDetail, VoteSummaryByPartyItem, VoteSummaryItem, PartyVote } from '../../api';
import { useAppContext } from '../../context/AppContext';
import { DateTime } from 'luxon';
import { SelectButton } from 'primereact/selectbutton';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { buildCouncilPath } from '../../utils/councilParams';
import VoteBar from '../../components/common/VoteBar/VoteBar';

type StatChipEntry = {
    icon: string;
    value: string | number;
    label: string;
    mutedText?: ReactNode;
};


const VotingDetail = () => {
    const [showLocalTime, setShowLocalTime] = useState(true);
    const { zastupitelstvoId, municipalitaId, bodyType } = useParams();
    const { t, i18n } = useTranslation(['votings', 'meetings', 'parties']);
    const { municipalityData } = useAppContext();

    const municipalityZone = municipalityData.casova_zona;
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    const voteData = useLoaderData() as VoteDetail;

    const voteColors = useVoteColors();

    const {voteOptionLabels} = useLocalizationLabels();

    const { voteMembers, partyGroups, voteLegend } = useMemo(() => {
        const detailByCode: Record<string, VoteSummaryByPartyItem | undefined> = Object.fromEntries(
            (voteData?.sumarizace_po_stranach ?? []).map((item) => [item.code, item])
        );

        const ranked: { item: PersonMatrixItem; partyKey: string; statusIndex: number }[] = [];
        const partyGroupMap = new Map<string, PersonMatrixGroup>();
        const statusLegendItems: PersonMatrixLegendItem[] = [];

        (voteData?.sumarizace ?? []).forEach((optionItem, statusIndex) => {
            const detailGroup = detailByCode[optionItem.code];
            const statusLabel = capitalizeFirstLetter(voteOptionLabels[optionItem.id]?.label ?? optionItem.code);
            const ringColor = voteColors[optionItem.code as keyof typeof voteColors] ?? getColors().darkGray;
            statusLegendItems.push({ color: ringColor, label: statusLabel, ring: true });

            detailGroup?.strany?.forEach((party: PartyVote) => {
                if (!partyGroupMap.has(party.strana_zkratka)) {
                    partyGroupMap.set(party.strana_zkratka, {
                        key: party.strana_zkratka,
                        label: party.strana_zkratka,
                        color: party.strana_barva
                    });
                }

                party.lide?.forEach((member) => {
                    ranked.push({
                        partyKey: party.strana_zkratka,
                        statusIndex,
                        item: {
                            id: member.id,
                            fullName: `${member.jmeno} ${member.prijmeni}`,
                            caption: member.prijmeni,
                            ringColor,
                            dotColor: party.strana_barva,
                            photoUrl: member.photo,
                            linkTo: buildCouncilPath(municipalitaId!, bodyType, zastupitelstvoId, 'zastupitele', member.id),
                            groupKey: party.strana_zkratka,
                            meta: [
                                { color: party.strana_barva, label: party.strana_zkratka },
                                { color: ringColor, label: statusLabel }
                            ]
                        }
                    });
                });
            });
        });

        const voteMembers = ranked
            .sort((a, b) => a.partyKey.localeCompare(b.partyKey) || a.statusIndex - b.statusIndex)
            .map((r) => r.item);

        const partyGroups = Array.from(partyGroupMap.values()).sort((a, b) => a.label.localeCompare(b.label));

        const voteLegend: PersonMatrixLegendGroup[] = [
            { label: t('VotingDetail.voteBreakdown'), items: statusLegendItems },
            {
                label: t('parties', { ns: 'parties' }),
                items: partyGroups.map((g) => ({ color: g.color ?? '', label: g.label }))
            }
        ];

        return { voteMembers, partyGroups, voteLegend };
    }, [voteData, voteColors, voteOptionLabels, municipalitaId, bodyType, zastupitelstvoId, t]);

    if (voteData !== undefined) {
        if (JSON.stringify(voteData) === '{"error":"neexistujici ID"}') {
            redirectErrorPage(municipalitaId, zastupitelstvoId);
        } else {
            const summaryVotesEntries: VoteSummaryItem[] = voteData?.sumarizace ?? [];

            const getVoteLabel = (item: VoteSummaryItem) => capitalizeFirstLetter(voteOptionLabels[item.id]?.label ?? item.code);

            const dtMunicipality = DateTime.fromISO(voteData?.datum_cas, { zone: municipalityZone });

            //User time
            const userZone = DateTime.local().zoneName;
            const dtUser = dtMunicipality.setZone(userZone);

            const formattedTimeUser = dtUser.setLocale(currentLocale).toLocaleString({...DateTime.TIME_WITH_SECONDS, timeZoneName: 'short'});
            const formattedDateUser = dtUser.setLocale(currentLocale).toLocaleString(DateTime.DATE_FULL);

            //Local municipality time
            const formattedTimeLocal = dtMunicipality.setLocale(currentLocale).toLocaleString({...DateTime.TIME_WITH_SECONDS, timeZoneName: 'short'});
            const formattedDateLocal = dtMunicipality.setLocale(currentLocale).toLocaleString(DateTime.DATE_FULL);

            //Toggle
            const formattedTime = showLocalTime ? formattedTimeLocal : formattedTimeUser;
            const formattedDate = showLocalTime ? formattedDateLocal : formattedDateUser;

            const isSameTime = dtMunicipality.offset === dtUser.offset;

            const result = voteData?.vysledek;

            const statChips: StatChipEntry[] = [];

            if (voteData?.pozmenovaci_hlasovani === 1) {
                statChips.push({ icon: 'edit', value: t('amendmentOption'), label: '' });
            }
            if (voteData?.proceduralni_hlasovani === 1) {
                statChips.push({ icon: 'settings', value: t('proceduralOption'), label: '' });
            }
            if (voteData?.validni_hlasovani === 0) {
                statChips.push({ icon: 'unpublished', value: t('invalidOption'), label: '' });
            }
            if (voteData?.tajne_hlasovani === 1) {
                statChips.push({ icon: 'lock', value: t('secretOption'), label: '' });
            }

            const timeToggleOptions = [
                { label: municipalityZone, value: true },
                { label: userZone, value: false }
            ];
            if (dtMunicipality.isValid) {
                statChips.push({
                    icon: 'calendar_today',
                    value: `${formattedDate}`,
                    label: `${formattedTime}`,
                    mutedText: !isSameTime ? (
                        <SelectButton
                            value={showLocalTime}
                            options={timeToggleOptions}
                            onChange={(e) => setShowLocalTime(e.value)}
                            className="time-toggle"
                        />
                    ) : undefined
                });
            }
            statChips.push({
                icon: result ? 'check_box' : 'disabled_by_default',
                value: result
                    ? capitalizeFirstLetter(t('VotingDetail.proposalAccepted'))
                    : capitalizeFirstLetter(t('VotingDetail.proposalRejected')),
                label: result
                    ? t('VotingDetail.proposalWasAccepted')
                    : t('VotingDetail.proposalWasRejected')
            });

            if (voteData?.pritomno !== 0) {
                statChips.push({
                    icon: 'group',
                    value: `${voteData?.pritomno}`,
                    label: `${t('VotingDetail.membersPresent')}`
                });
            }

            return (
                <>
                    <div className='page-header-row'>
                        <ContentSection
                            title={`${t('VotingDetail.votingNo')} ${voteData.cislo_hlasovani}`}
                            level={1}
                            subtitle={`${t('meetingNo', { ns: 'meetings', num: voteData.cislo_zasedani })}`}
                            className='page-title-section'
                            showMoreChevronLinkProps={
                                voteData?.soubor
                                    ? {
                                            link: `${voteData.soubor}`,
                                            text: `${capitalizeFirstLetter(t('VotingDetail.votingProtocol'))}`
                                        }
                                    : undefined
                            }
                        />
                        <div className='stat-chip-row'>
                            {statChips.map((chip, index) => (
                                <StatChip
                                    key={index}
                                    icon={chip.icon}
                                    value={chip.value}
                                    label={chip.label}
                                    mutedText={chip.mutedText}
                                />
                            ))}
                        </div>
                    </div>
                    <div className='page-description'>
                        {voteData.predmet_hlasovani ? voteData.predmet_hlasovani : <i>*&nbsp;{t('votingWithoutDescription')}&nbsp;*</i>}
                    </div>
                    <ContentSection level={3} title={t('VotingDetail.voteBreakdown')} subtitle={t('tooltipChart', { ns: 'translation' })} className='dashboard-card'>
                        <VoteBar
                            items={summaryVotesEntries}
                            colors={voteColors}
                            getLabel={getVoteLabel}
                            formatPercent={formatter.percentFrom100}
                        />
                    </ContentSection>
                    <ContentSection level={3} title={t('VotingDetail.howMembersVoted')} subtitle={t('VotingDetail.howMembersVotedTooltip')} className='dashboard-card'>
                        <PersonMatrix items={voteMembers} groups={partyGroups} legend={voteLegend} />
                    </ContentSection>
                </>
            );
        }
    }
};

export default VotingDetail;
